/**
 * The 'ch-paginator' component represents a paginator control for navigating through pages.
 */
export class ChPaginator {
    constructor() {
        this.activePage = 1;
        this.totalPages = 1;
        this.hasNextPage = false;
    }
    elPages;
    elFirst;
    elPrevious;
    elNext;
    elLast;
    el;
    activePageHandler() {
        this.activePageChanged.emit({ activePage: this.activePage });
    }
    /**
     * Event emitted when the active page changes.
     */
    activePageChanged;
    /**
     * Event emitted when the navigation is requested.
     */
    pageNavigationRequested;
    componentWillLoad() {
        this.loadElements();
    }
    navigateClickedHandler(eventInfo) {
        eventInfo.stopPropagation();
        this.navigate(eventInfo.detail.type);
    }
    pageChangedHandler(eventInfo) {
        eventInfo.stopPropagation();
        this.navigate("goto", eventInfo.detail.page);
    }
    keyDownHandler(eventInfo) {
        switch (eventInfo.key) {
            case "Home":
                this.navigate("first");
                break;
            case "PageUp":
                this.navigate("previous");
                break;
            case "PageDown":
                this.navigate("next");
                break;
            case "End":
                this.navigate("last");
                break;
        }
    }
    navigate(type, page) {
        const emitPageNavigationRequested = !page || page !== this.activePage;
        switch (type) {
            case "first":
                this.first();
                this.elFirst?.focus();
                break;
            case "previous":
                this.previous();
                this.elPrevious?.focus();
                break;
            case "next":
                this.next();
                this.elNext?.focus();
                break;
            case "last":
                this.last();
                this.elLast?.focus();
                break;
            case "goto":
                this.goto(page);
                break;
        }
        if (emitPageNavigationRequested) {
            this.pageNavigationRequested.emit({ type, page });
        }
    }
    first() {
        this.activePage = 1;
    }
    previous() {
        this.activePage = Math.max(this.activePage - 1, 1);
    }
    next() {
        this.activePage =
            this.totalPages < 0
                ? this.activePage + 1
                : Math.min(this.activePage + 1, this.totalPages);
    }
    last() {
        this.activePage = this.totalPages;
    }
    goto(page) {
        this.activePage = Math.max(Math.min(page, this.totalPages), 1);
    }
    loadElements() {
        this.elPages = this.el.querySelector("ch-paginator-pages");
        this.el
            .querySelectorAll("ch-paginator-navigate")
            .forEach((el) => {
            switch (el.type) {
                case "first":
                    this.elFirst = el;
                    break;
                case "previous":
                    this.elPrevious = el;
                    break;
                case "next":
                    this.elNext = el;
                    break;
                case "last":
                    this.elLast = el;
                    break;
            }
        });
    }
    render() {
        if (this.elPages) {
            this.elPages.totalPages = this.totalPages;
            this.elPages.page = this.activePage;
        }
        if (this.elFirst) {
            this.elFirst.disabled = this.activePage === 1;
        }
        if (this.elPrevious) {
            this.elPrevious.disabled = this.activePage === 1;
        }
        if (this.elNext) {
            this.elNext.disabled =
                this.activePage === this.totalPages ||
                    (this.totalPages < 0 && !this.hasNextPage);
        }
        if (this.elLast) {
            this.elLast.disabled =
                this.activePage === this.totalPages ||
                    (this.totalPages < 0 && !this.hasNextPage);
        }
    }
    static get is() { return "ch-paginator"; }
    static get originalStyleUrls() {
        return {
            "$": ["ch-paginator.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["ch-paginator.css"]
        };
    }
    static get properties() {
        return {
            "activePage": {
                "type": "number",
                "mutable": true,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The active page number."
                },
                "attribute": "active-page",
                "reflect": true,
                "defaultValue": "1"
            },
            "totalPages": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "1",
                    "resolved": "1",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The total number of pages.\nUse -1 if not known and 'hasNextPage' property to indicate\nthat the end has been reached."
                },
                "attribute": "total-pages",
                "reflect": false,
                "defaultValue": "1"
            },
            "hasNextPage": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Indicates that the end has been reached.\nUse when total pages are not known (totalPages = -1)."
                },
                "attribute": "has-next-page",
                "reflect": false,
                "defaultValue": "false"
            }
        };
    }
    static get events() {
        return [{
                "method": "activePageChanged",
                "name": "activePageChanged",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Event emitted when the active page changes."
                },
                "complexType": {
                    "original": "ChPaginatorActivePageChangedEvent",
                    "resolved": "ChPaginatorActivePageChangedEvent",
                    "references": {
                        "ChPaginatorActivePageChangedEvent": {
                            "location": "local",
                            "path": "/home/runner/work/chameleon-controls-library/chameleon-controls-library/src/components/paginator/ch-paginator.tsx",
                            "id": "src/components/paginator/ch-paginator.tsx::ChPaginatorActivePageChangedEvent"
                        }
                    }
                }
            }, {
                "method": "pageNavigationRequested",
                "name": "pageNavigationRequested",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Event emitted when the navigation is requested."
                },
                "complexType": {
                    "original": "ChPaginatorPageNavigationRequestedEvent",
                    "resolved": "ChPaginatorPageNavigationRequestedEvent",
                    "references": {
                        "ChPaginatorPageNavigationRequestedEvent": {
                            "location": "local",
                            "path": "/home/runner/work/chameleon-controls-library/chameleon-controls-library/src/components/paginator/ch-paginator.tsx",
                            "id": "src/components/paginator/ch-paginator.tsx::ChPaginatorPageNavigationRequestedEvent"
                        }
                    }
                }
            }];
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "activePage",
                "methodName": "activePageHandler"
            }];
    }
    static get listeners() {
        return [{
                "name": "navigateClicked",
                "method": "navigateClickedHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "pageChanged",
                "method": "pageChangedHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "keydown",
                "method": "keyDownHandler",
                "target": undefined,
                "capture": false,
                "passive": true
            }];
    }
}
//# sourceMappingURL=ch-paginator.js.map
